import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { GoogleMap, OverlayView } from '@react-google-maps/api';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { getLatLong } from '../common/helpers';

const containerStyle = { width: '100%', height: '300px', borderRadius: '12px' };
const DEFAULT_CENTER = { lat: 23.6345, lng: -102.5528 }; // Centro de México

// Cache de geocodificación por dirección (persiste durante la sesión)
const geoCache = new Map();

// getLatLong no resuelve la promesa cuando el geocode falla → la blindamos con timeout
const geocodeSafe = (dir) => {
  if (geoCache.has(dir)) return Promise.resolve(geoCache.get(dir));
  return Promise.race([
    getLatLong(dir).then(c => c || null).catch(() => null),
    new Promise(res => setTimeout(() => res(null), 5000)),
  ]).then(coords => {
    geoCache.set(dir, coords);
    return coords;
  });
};

const SearchMap = ({ servicios = [] }) => {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [mapsReady, setMapsReady] = useState(!!(window.google && window.google.maps));
  const mapRef = useRef(null);

  // El script de Google Maps se carga en index.html; esperar a que esté listo antes de montar el mapa
  useEffect(() => {
    if (mapsReady) return;
    const id = setInterval(() => {
      if (window.google && window.google.maps) {
        setMapsReady(true);
        clearInterval(id);
      }
    }, 300);
    return () => clearInterval(id);
  }, [mapsReady]);

  // Clave estable: solo cambia cuando cambian las direcciones únicas (no al ordenar/filtrar precio)
  const direccionesUnicas = [...new Set(
    servicios.map(s => (s.ubicacion || '').trim()).filter(Boolean)
  )];
  const dirKey = [...direccionesUnicas].sort().join('|');

  useEffect(() => {
    let cancelled = false;

    const porDireccion = {};
    servicios.forEach(s => {
      const dir = (s.ubicacion || '').trim();
      if (!dir) return;
      if (!porDireccion[dir]) porDireccion[dir] = [];
      porDireccion[dir].push(s);
    });

    const direcciones = Object.keys(porDireccion).slice(0, 30);
    if (direcciones.length === 0) {
      setMarkers([]);
      return;
    }

    setLoading(true);
    Promise.all(direcciones.map(async (dir) => {
      const coords = await geocodeSafe(dir);
      return coords ? { coords, direccion: dir, items: porDireccion[dir] } : null;
    })).then(res => {
      if (cancelled) return;
      setMarkers(res.filter(Boolean));
      setActive(null);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [dirKey]);

  // Ajustar el encuadre del mapa a todos los marcadores
  useEffect(() => {
    if (!mapRef.current || !window.google || markers.length === 0) return;
    try {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach(m => bounds.extend(m.coords));
      mapRef.current.fitBounds(bounds);
      if (markers.length === 1) mapRef.current.setZoom(15);
    } catch (e) { /* noop */ }
  }, [markers, mapsReady]);

  return (
    <Box sx={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', mb: 4, border: '1px solid #F1F5F9' }}>
      {mapsReady ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={DEFAULT_CENTER}
          zoom={5}
          onLoad={(map) => { mapRef.current = map; }}
          onUnmount={() => { mapRef.current = null; }}
          options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
        >
          {markers.map((m, i) => (
            <OverlayView key={i} position={m.coords} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
              <div
                style={{ transform: 'translate(-50%, -100%)', cursor: 'pointer', position: 'relative' }}
                onClick={() => setActive(active === i ? null : i)}
              >
                <LocationOnIcon style={{ color: '#0E2465', fontSize: 38, filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.35))' }} />
                {active === i && (
                  <div style={{
                    position: 'absolute', bottom: 42, left: '50%', transform: 'translateX(-50%)',
                    background: 'white', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                    padding: '10px 12px', width: 210, zIndex: 1000
                  }}>
                    {m.items.slice(0, 4).map(s => (
                      <Typography key={s._id} variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#0E2465' }}>
                        {s.nombre} — ${Number(s.costoServicio).toFixed(0)}
                      </Typography>
                    ))}
                    {m.items.length > 4 && (
                      <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                        +{m.items.length - 4} servicio(s) más
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ display: 'block', color: '#737791', mt: 0.5 }}>
                      {m.direccion}
                    </Typography>
                  </div>
                )}
              </div>
            </OverlayView>
          ))}
        </GoogleMap>
      ) : (
        <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
          <CircularProgress size={24} sx={{ color: '#7495f8' }} />
        </Box>
      )}

      {loading && (
        <Box sx={{ position: 'absolute', top: 10, right: 10, bgcolor: 'white', borderRadius: '50%', p: 0.6, boxShadow: 1 }}>
          <CircularProgress size={18} sx={{ color: '#7495f8' }} />
        </Box>
      )}

      {mapsReady && !loading && markers.length === 0 && (
        <Box sx={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(255,255,255,0.75)', pointerEvents: 'none', textAlign: 'center', px: 2
        }}>
          <Typography variant="body2" color="#737791">
            Ningún servicio de la búsqueda tiene una dirección para mostrar en el mapa.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SearchMap;
